'use client';

import { useMemo } from 'react';
import { useTranslate } from '@tolgee/react';
import { ActionEnum, SubjectEnum } from 'abi-genome-common-types';

import { RouteObject } from '~/app/types';
import { useAbility } from '~/contexts/AbilityContext';
import { useGetPendingTicketSummary } from '~/features/ticket/hooks/useGetPendingTicketSummary';
import { ALL_REQUESTS_PATH, getTicketListPath } from '~/shared/auth/ticket-list-path';
import { useUser } from '~/shared/auth/useUser';
import { Icon } from '~/shared/components/Icon';
import {
  EUR_CREATE_TICKET_LINK,
  EUR_POLICIES_LINK,
  GHQ_POLICIES_LINK,
  GQH_CREATE_TICKET_LINK,
} from '~/shared/constants/sidebar';

export function useMenuRoutes() {
  const { t } = useTranslate();
  const ability = useAbility();
  const { data: pendingTicketSummary } = useGetPendingTicketSummary();

  const { zone, canAccessApprovalConfiguration, isAdmin, isApprover, hasVisibleApprovals } = useUser();

  const policiesLink =
    zone === 'ZONE EUROPE' ? EUR_POLICIES_LINK : GHQ_POLICIES_LINK;
  const createTicketLink =
    zone === 'ZONE EUROPE' ? EUR_CREATE_TICKET_LINK : GQH_CREATE_TICKET_LINK;
  const { menuRoutes, footerRoutes } = useMemo(() => {
    const menuRoutesList: RouteObject[] = [];
    const footerRoutesList: RouteObject[] = [];

    menuRoutesList.push({
      key: 'home',
      icon: <Icon isCustom icon="house" size={18} />,
      path: '/home',
      title: t('sidebar.home'),
    });

    const canList = ability.can(ActionEnum.List, SubjectEnum.Ticket);
    const requestsPath = getTicketListPath({
      isApprover,
      isAdmin,
      hasVisibleApprovals,
    });

    if (canList) {
      menuRoutesList.push({
        key: requestsPath === ALL_REQUESTS_PATH ? 'all-requests' : 'my-requests',
        icon: <Icon icon="ticket" isCustom size={18} />,
        path: requestsPath,
        title: t('sidebar.requests'),
        showNotificationBadge: !!pendingTicketSummary?.myRequests,
      });
    }

    menuRoutesList.push({
      key: 'policies',
      icon: <Icon icon="policies" isCustom size={18} />,
      path: policiesLink,
      title: t('sidebar.policies'),
    });

    if (
      isAdmin &&
      ability.can(ActionEnum.View, SubjectEnum.RoleSettings)
    ) {
      const roleSettingsRoutes: RouteObject[] = [];

      if (ability.can(ActionEnum.Create, SubjectEnum.Team)) {
        roleSettingsRoutes.push({
          key: 'create-team',
          path: '/admin/create-team',
          title: t('sidebar.create_team'),
        });
      }

      if (ability.can(ActionEnum.View, SubjectEnum.ManageTeam)) {
        roleSettingsRoutes.push({
          key: 'manage-teams',
          path: '/admin/manage-teams',
          title: t('sidebar.manage_teams'),
        });
      }

      if (ability.can(ActionEnum.View, SubjectEnum.ManageEmployee)) {
        roleSettingsRoutes.push({
          key: 'manage-employees',
          path: '/admin/manage-employees',
          title: t('sidebar.manage_employees'),
        });
      }

      if (ability.can(ActionEnum.View, SubjectEnum.ManagePermissions)) {
        roleSettingsRoutes.push({
          key: 'manage-permissions',
          path: '/admin/manage-permissions',
          title: t('sidebar.manage_permissions'),
        });
      }

      if (roleSettingsRoutes.length > 0) {
        menuRoutesList.push({
          key: 'role-settings',
          title: t('sidebar.role_settings'),
          icon: <Icon icon="settings" isCustom size={18} />,
          routeComponents: roleSettingsRoutes,
        });
      }
    }

    if (canAccessApprovalConfiguration) {
      menuRoutesList.push({
        key: 'approval-configuration',
        icon: <Icon icon="checkMark" isCustom size={18} />,
        path: '/admin/approval-configuration',
        title: t('sidebar.approval_configuration'),
      });
    }

    const canCreateDelegation = ability.can(
      ActionEnum.Create,
      SubjectEnum.CreateDelegation
    );
    const canManageDelegations = ability.can(
      ActionEnum.View,
      SubjectEnum.ManageDelegations
    );
    if (canManageDelegations || canCreateDelegation) {
      const delegationSettingsRoutes: RouteObject[] = [];

      if (canCreateDelegation) {
        delegationSettingsRoutes.push({
          key: 'create-delegation',
          path: '/delegate-settings/create',
          title: t('sidebar.create_delegation'),
        });
      }

      if (canManageDelegations) {
        delegationSettingsRoutes.push({
          key: 'manage-delegations',
          path: '/delegate-settings/manage-delegations',
          title: t('sidebar.manage_delegation'),
        });
      }

      if (delegationSettingsRoutes.length > 0) {
        menuRoutesList.push({
          key: 'delegation-settings',
          title: t('sidebar.delegation'),
          icon: <Icon icon="arrowDiagonal" isCustom size={18} />,
          routeComponents: delegationSettingsRoutes,
        });
      }
    }

    footerRoutesList.push({
      key: 'knowledgeCenter',
      icon: <Icon isCustom icon="helpCenter" size={18} />,
      title: t('sidebar.knowledge_center'),
      routeComponents: [
        {
          key: 'how-to-create-ticket',
          helpCenterDialog: true,
          path: '',
          title: t('sidebar.knowledge_center.how_to_create_a_ticket'),
        },
        {
          key: 'create-a-ticket',
          path: createTicketLink,
          title: t('sidebar.knowledge_center.create_a_ticket'),
          shouldOpenInNewTab: true,
        },
      ],
    });

    return { menuRoutes: menuRoutesList, footerRoutes: footerRoutesList };
  }, [
    t,
    ability,
    policiesLink,
    createTicketLink,
    pendingTicketSummary,
    canAccessApprovalConfiguration,
    isAdmin,
    isApprover,
    hasVisibleApprovals,
  ]);

  return { menuRoutes, footerRoutes };
}
